import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ShellHeader from '../components/ShellHeader';
import { useVehicles } from '../context/VehiclesContext';
import { formatEngineSizeForDisplay } from '../utils/engineSize';
import { auth } from '../firebase';
import { compressImages, prepareImages } from '../utils/compressImage';
import ReorderableImageGrid from '../components/ReorderableImageGrid';

const initialFormState = {
  make: '',
  model: '',
  year: '',
  mileage: '',
  vehicleType: '',
  fuelType: '',
  gearboxType: '',
  engineSize: '',
  registrationNumber: '',
  salePrice: '',
  financePrice: '',
  description: '',
};

function calculateFinancePrice(salePrice) {
  if (!salePrice || isNaN(salePrice)) return '';
  const principal = parseFloat(salePrice);
  const totalAmount = principal * 1.15; // 15% interest added to principal
  const monthlyPayment = totalAmount / 36; // 3 years = 36 months
  return monthlyPayment.toFixed(2);
}

function EditVehiclePage({ authUser }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refreshVehicles } = useVehicles();
  const [vehicle, setVehicle] = useState(null);
  const [form, setForm] = useState(initialFormState);
  // Ordered photo list: saved photos have only a url, newly picked ones also carry a file.
  const [items, setItems] = useState([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const [manufacturers, setManufacturers] = useState([]);
  const [addManufacturerSubmitting, setAddManufacturerSubmitting] = useState(false);
  const [isMakeFocused, setIsMakeFocused] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const makeValue = String(form.make || '');
  const normalizedMakeValue = makeValue.trim().toLowerCase();

  const filteredManufacturers = useMemo(() => {
    if (!normalizedMakeValue) {
      return manufacturers.slice(0, 120);
    }

    return manufacturers
      .filter((name) => String(name).toLowerCase().includes(normalizedMakeValue))
      .slice(0, 120);
  }, [manufacturers, normalizedMakeValue]);

  const hasExactManufacturerMatch =
    normalizedMakeValue.length > 0 &&
    manufacturers.some((name) => String(name).trim().toLowerCase() === normalizedMakeValue);
  const engineSizeDisplayValue = formatEngineSizeForDisplay(form.engineSize);

  useEffect(() => {
    const loadVehicle = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/vehicles/${id}`);

        if (response.status === 404) {
          setMessage('Vehicle not found.');
          setVehicle(null);
          return;
        }

        if (!response.ok) {
          throw new Error('Failed to load vehicle details.');
        }

        const data = await response.json();
        setVehicle(data);
        setItems(
          (data.imageUrls || [data.imageUrl])
            .filter(Boolean)
            .map((url) => ({ id: url, url, file: null }))
        );
        const loadedForm = {
          make: data.make || '',
          model: data.model || '',
          year: data.year ?? '',
          mileage: data.mileage ?? '',
          vehicleType: data.vehicleType || 'car',
          fuelType: data.fuelType || '',
          gearboxType: data.gearboxType || '',
          engineSize: data.engineSize || '',
          registrationNumber: data.registrationNumber || '',
          salePrice: data.salePrice ?? '',
          financePrice: data.financePrice ?? '',
          description: data.description || '',
        };
        loadedForm.financePrice = calculateFinancePrice(loadedForm.salePrice);
        setForm(loadedForm);
      } catch (error) {
        setMessage(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadVehicle();
  }, [id]);

  useEffect(() => {
    const loadManufacturers = async () => {
      try {
        const response = await fetch('/api/manufacturers');
        if (!response.ok) {
          throw new Error('Failed to load manufacturers.');
        }

        const payload = await response.json();
        const manufacturerList = Array.isArray(payload.manufacturers) ? payload.manufacturers : [];
        setManufacturers(manufacturerList);
      } catch (error) {
        setMessage(error.message);
      }
    };

    loadManufacturers();
  }, []);

  useEffect(() => () => {
    itemsRef.current.forEach((item) => {
      if (item.file) URL.revokeObjectURL(item.url);
    });
  }, []);

  const moveImage = (from, to) => {
    setItems((current) => {
      if (to < 0 || to >= current.length) return current;
      const next = [...current];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const removeImage = (index) => {
    const target = itemsRef.current[index];
    if (target?.file) URL.revokeObjectURL(target.url);
    setItems((current) => current.filter((_, i) => i !== index));
  };

  const authorizedFetch = async (url, options = {}) => {
    if (!auth.currentUser) {
      throw new Error('Please log in again.');
    }

    const token = await auth.currentUser.getIdToken();
    return fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    });
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => {
      const updated = { ...current, [name]: value };
      if (name === 'salePrice') {
        updated.financePrice = calculateFinancePrice(value);
      }
      return updated;
    });
  };

  const handleFileChange = async (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = '';
    if (picked.length > 0) {
      setMessage('Preparing photos...');
      const { ready: files, failed } = await prepareImages(picked);
      setMessage(failed > 0 ? `${failed} photo(s) could not be read and were skipped.` : '');
      const room = Math.max(0, 20 - itemsRef.current.filter((item) => item.file).length);
      const added = files.slice(0, room).map((file) => ({
        id: `new-${Date.now()}-${Math.random()}`,
        url: URL.createObjectURL(file),
        file,
        name: file.name,
      }));
      setItems((current) => [...current, ...added]);
    }
  };

  const selectMake = (name) => {
    setForm((current) => ({
      ...current,
      make: name,
    }));
    setIsMakeFocused(false);
  };

  const handleAddManufacturer = async (rawName) => {
    const name = String(rawName || '').trim();

    if (name.length < 2) {
      setMessage('Please enter at least 2 characters for manufacturer name.');
      return;
    }

    const alreadyExists = manufacturers.some(
      (manufacturer) => manufacturer.trim().toLowerCase() === name.toLowerCase()
    );

    if (alreadyExists) {
      setMessage('Manufacturer already exists in the list.');
      return;
    }

    try {
      setAddManufacturerSubmitting(true);

      const response = await authorizedFetch('/api/manufacturers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name }),
      });

      const payload = await response.json();

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('You do not have admin privileges to add manufacturers.');
        }

        throw new Error(payload.error || 'Could not add manufacturer.');
      }

      const resolvedName = String(payload.name || name).trim();

      setManufacturers((current) => {
        const next = [...current];
        if (!next.some((item) => item.trim().toLowerCase() === resolvedName.toLowerCase())) {
          next.push(resolvedName);
          next.sort((a, b) => a.localeCompare(b));
        }
        return next;
      });

      setForm((current) => ({
        ...current,
        make: resolvedName,
      }));

      setMessage(
        payload.created === false
          ? `Manufacturer already existed: ${resolvedName}`
          : `Manufacturer added: ${resolvedName}`
      );
    } catch (error) {
      setMessage(error.message);
    } finally {
      setAddManufacturerSubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage('');
    setSaving(true);

    try {
      let response;
      const payload = {
        make: form.make,
        model: form.model,
        year: form.year,
        mileage: form.mileage,
        vehicleType: form.vehicleType,
        salePrice: form.salePrice,
        financePrice: form.financePrice,
        description: form.description,
        fuelType: form.fuelType,
        gearboxType: form.gearboxType,
        engineSize: form.engineSize,
        registrationNumber: form.registrationNumber,
      };

      const newFiles = items.filter((item) => item.file).map((item) => item.file);
      const savedUrls = (vehicle?.imageUrls || [vehicle?.imageUrl]).filter(Boolean);
      let newIndex = 0;
      // Saved photos are referenced by url, new ones by their position in the uploaded files.
      const imageOrder = items.map((item) => (item.file ? `__new__:${newIndex++}` : item.url));
      const removedImageUrls = savedUrls.filter((url) => !imageOrder.includes(url));

      if (newFiles.length > 0) {
        const formData = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value !== '' && value !== null && value !== undefined) {
            formData.append(key, value);
          }
        });

        const compressedImages = await compressImages(newFiles);
        compressedImages.forEach((image) => formData.append('images', image));
        formData.append('imageOrder', JSON.stringify(imageOrder));
        formData.append('removedImageUrls', JSON.stringify(removedImageUrls));

        response = await authorizedFetch(`/api/vehicles/${id}`, {
          method: 'PATCH',
          body: formData,
        });
      } else {
        response = await authorizedFetch(`/api/vehicles/${id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...payload,
            imageOrder,
            removedImageUrls,
          }),
        });
      }

      if (!response.ok) {
        const payloadResponse = await response.json();
        throw new Error(payloadResponse.error || 'Unable to save vehicle.');
      }

      await refreshVehicles();
      navigate('/admin', { replace: true });
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="site-shell">
      <ShellHeader authUser={authUser} />

      <main className="content-wrap content-wrap-single">
        <section className="vehicle-details-block">
          <div className="section-header">
            <h2>Edit Vehicle</h2>
            <Link to="/admin" className="ghost-btn vehicle-back-link">Back to Admin</Link>
          </div>

          {loading && <p className="status-text">Loading vehicle...</p>}
          {!loading && message && <p className="status-text">{message}</p>}

          {!loading && vehicle && (
            <form className="upload-form" onSubmit={handleSubmit}>
              <label htmlFor="edit-vehicle-make" className="make-list-label">Make</label>
              <div className="make-search-wrap">
                <input
                  id="edit-vehicle-make"
                  className="make-search-input"
                  type="text"
                  name="make"
                  placeholder="Start typing manufacturer"
                  value={form.make}
                  onChange={handleInputChange}
                  onFocus={() => setIsMakeFocused(true)}
                  onBlur={() => {
                    setTimeout(() => setIsMakeFocused(false), 120);
                  }}
                  autoComplete="off"
                  required
                />

                {isMakeFocused && filteredManufacturers.length > 0 && (
                  <div className="make-search-dropdown" role="listbox" aria-label="Manufacturer suggestions">
                    {filteredManufacturers.map((name) => (
                      <button
                        key={name}
                        type="button"
                        className="make-search-option"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          selectMake(name);
                        }}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {normalizedMakeValue && !hasExactManufacturerMatch && (
                <button
                  type="button"
                  className="make-picker-add-btn"
                  onClick={() => handleAddManufacturer(makeValue)}
                  disabled={addManufacturerSubmitting}
                >
                  {addManufacturerSubmitting
                    ? 'Adding Manufacturer...'
                    : `Add "${makeValue.trim()}" to manufacturers`}
                </button>
              )}
              <input
                type="text"
                name="model"
                placeholder="Model"
                value={form.model}
                onChange={handleInputChange}
                required
              />
              <input
                type="number"
                name="year"
                placeholder="Year"
                value={form.year}
                onChange={handleInputChange}
                required
              />
              <input
                type="number"
                name="mileage"
                placeholder="Mileage"
                value={form.mileage}
                onChange={handleInputChange}
                required
              />
              <select
                name="vehicleType"
                value={form.vehicleType}
                onChange={handleInputChange}
                required
              >
                <option value="">Vehicle type</option>
                <option value="car">Car</option>
                <option value="van">Van</option>
                <option value="pickup">Pickup</option>
                <option value="motorhome">Motorhome</option>
                <option value="other">Other</option>
              </select>
              <select
                name="fuelType"
                value={form.fuelType}
                onChange={handleInputChange}
                required
              >
                <option value="">Fuel type</option>
                <option value="petrol">Petrol</option>
                <option value="diesel">Diesel</option>
                <option value="electric">Electric</option>
              </select>
              <select
                name="gearboxType"
                value={form.gearboxType}
                onChange={handleInputChange}
                required
              >
                <option value="">Gearbox</option>
                <option value="manual">Manual</option>
                <option value="automatic">Automatic</option>
              </select>
              <input
                type="text"
                name="engineSize"
                placeholder="Engine size"
                value={form.engineSize}
                onChange={handleInputChange}
                required
              />
              {String(form.engineSize || '').trim().length > 0 && (
                <p className="status-text">Displays as: {engineSizeDisplayValue}</p>
              )}
              <input
                type="text"
                name="registrationNumber"
                placeholder="Registration number"
                value={form.registrationNumber}
                onChange={handleInputChange}
                required
              />
              <input
                type="number"
                name="salePrice"
                placeholder="Sale price (£)"
                value={form.salePrice}
                onChange={handleInputChange}
                required
              />
              <input
                type="number"
                name="financePrice"
                placeholder="Finance price (£)"
                value={form.financePrice}
                readOnly
              />
              <textarea
                name="description"
                placeholder="Vehicle description"
                value={form.description}
                onChange={handleInputChange}
                rows="4"
                required
              />

              <div className="current-images-preview">
                <h3>Photos</h3>
                <p className="status-text">Drag photos (or use the arrows) to reorder. The first photo is the main image.</p>
                {items.length === 0 ? (
                  <p className="status-text">No photos selected.</p>
                ) : (
                  <ReorderableImageGrid
                    items={items.map((item, index) => ({
                      id: item.id,
                      url: item.url,
                      alt: item.name || `${vehicle.make} ${vehicle.model} ${index + 1}`,
                    }))}
                    onMove={moveImage}
                    onRemove={removeImage}
                  />
                )}
              </div>

              <p className="status-text">Add more photos below (up to 20 at a time). New photos are added to the end of the list.</p>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                multiple
              />

              <button type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save vehicle'}
              </button>
            </form>
          )}
        </section>
      </main>
    </div>
  );
}

export default EditVehiclePage;
